import React from 'react';
import { Link } from 'react-router-dom';
import { useUserRole } from '../../contexts/UserRoleContext';
import { NAV_VISIBLE_SECTIONS } from 'config/sections';
import './Navigation.css';

interface NavigationProps {
  onSectionChange: (section: string) => void;
  /** Slug for current location (e.g. "oakland"); null when no location selected */
  locationSlug: string | null;
  /** Current pathname for building links and aria-current */
  currentPath: string;
}

/**
 * Seven base menu swatches in bar order (Overview … GHG Scenarios).
 * Additional items (Calculator, Analysis, Policies, …) repeat this cycle by index.
 * Matches CSS classes in Navigation.css.
 */
const MAIN_NAV_COLOR_CYCLE = ['blue', 'light-blue', 'gold', 'orange', 'pink', 'teal', 'scenarios'] as const;

type NavColor = (typeof MAIN_NAV_COLOR_CYCLE)[number];

type NavItem = {
  section: string;
  label: string;
  shortLabel: string;
  color: NavColor;
};

type NavItemInput = Omit<NavItem, 'color'>;

function assignNavColors(items: NavItemInput[]): NavItem[] {
  return items.map((item, index) => ({
    ...item,
    color: MAIN_NAV_COLOR_CYCLE[index % MAIN_NAV_COLOR_CYCLE.length],
  }));
}

function buildSectionPath(slug: string | null, section: string): string {
  if (section === 'law-impact') return '/law-impact';
  if (section === 'projects') return '/projects';
  if (!slug) return section === 'overview' ? '/overview' : `/${section}`;
  return section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange: _onSectionChange, locationSlug, currentPath }) => {
  const { hasAnyRole } = useUserRole();

  const baseNavItems: NavItemInput[] = [
    { section: 'overview', label: 'Overview', shortLabel: 'Overview' },
    { section: 'transportation', label: 'Transportation', shortLabel: 'Transport' },
    { section: 'buildings', label: 'Buildings', shortLabel: 'Buildings' },
    { section: 'other', label: 'Other', shortLabel: 'Other' },
    { section: 'people', label: 'People', shortLabel: 'People' },
    { section: 'ghg', label: 'GHG inventory', shortLabel: 'GHG' },
  ];

  const publicNavItems: NavItemInput[] = [
    ...baseNavItems,
    { section: 'policy-tool', label: 'GHG Scenarios', shortLabel: 'Scenarios' },
  ];

  const showCalculator = hasAnyRole(['super_admin', 'developer']);
  let navItemsInput: NavItemInput[] = [...publicNavItems];
  if (showCalculator) {
    navItemsInput = [...navItemsInput, { section: 'coolclimate-calculator', label: 'Calculator', shortLabel: 'Calc' }];
  }
  navItemsInput = [
    ...navItemsInput,
    { section: 'analysis', label: 'Analysis', shortLabel: 'Analysis' },
    { section: 'law-impact', label: 'Policies', shortLabel: 'Policies' },
    { section: 'projects', label: 'Projects', shortLabel: 'Projects' },
  ];

  const navItems = assignNavColors(navItemsInput);

  const handleNavClick = (section: string, e: React.MouseEvent | React.KeyboardEvent) => {
    const knownSections = NAV_VISIBLE_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    const isOverviewPath =
      parts.length === 0 ||
      (parts.length === 1 && !knownSections.has(parts[0])) ||
      (parts.length === 2 && parts[1] === 'overview');
    const isOnOverviewPage = isOverviewPath;
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;

    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      e.preventDefault();
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const knownSections = NAV_VISIBLE_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    if (parts.length === 0) return 'overview';
    if (parts.length === 1) return knownSections.has(parts[0]) ? parts[0] : 'overview';
    return parts[1] || 'overview';
  };

  const currentSection = getCurrentSection();

  return (
    <nav className="nav-bar" aria-label="Main navigation">
      <div className="nav-items" role="list">
        {navItems.map((item) => {
          const path = buildSectionPath(locationSlug, item.section);
          const isActive = currentSection === item.section;
          const aria = item.label.trim();
          return (
            <Link
              key={item.section}
              to={path}
              className={`nav-item ${item.color} ${isActive ? 'active' : ''}`}
              onClick={(e) => handleNavClick(item.section, e)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={aria}
              role="listitem"
            >
              <span className="nav-item-label nav-item-label--full">{item.label}</span>
              <span className="nav-item-label nav-item-label--compact">{item.shortLabel}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
